import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { VehicleCurrentInfo } from '../../../models/vehicle';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components'; 
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';

@Component({
  selector: 'app-current-sohc-chart-ef',
  standalone: true,
  imports: [CommonModule,RbUiComponentsModule],
  templateUrl: './current-sohc-chart-ef.component.html',
  styleUrls: ['./current-sohc-chart-ef.component.scss']
})
export class CurrentSohcChartEfComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() selectedVehicles: any[] = [];
  currentVehicleData: any;
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  vehiclesInfo!: { [key: string]: VehicleCurrentInfo; };
  chartRange!: { range: [number, number] };

  constructor(
    private readonly fleetGeneralFigureService: FleetGeneralFigureService,
    private readonly vehicleInfoService: VehicleInfoService,
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetGeneralFigureService.getSohcFleet(this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (datapoints) => {
        const fleetData = this.plotCurrentSohcChart(datapoints);
        this.fetchVehicleInfoAndUpdateChart(fleetData);
      },
      error: (error) => {
        console.error('Error fetching Current State of Health Fleet data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    });
  }

  fetchVehicleInfoAndUpdateChart(fleetData: any[]): void {
    const deviceSerialNumber: string[] = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    const vehicleNameMap: { [key: string]: string } = {};

    this.selectedVehicles.forEach(vehicle => {
      vehicleNameMap[vehicle.batteryIdentifier] = vehicle.name;
    });

    this.vehicleInfoService.getCurrentVehicleInfo(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data) => {
        this.vehiclesInfo = data;

        const averageLines = deviceSerialNumber.map((id, index) => {
          const averageEVEfficiency = this.vehiclesInfo[id].batteryStateOfHealth;
          const vehicleName = vehicleNameMap[id];

          const maxPercentAmount = Math.max(...fleetData.flatMap(plot => plot.y));

          return {
            x: [averageEVEfficiency, averageEVEfficiency],
            y: [0, maxPercentAmount],
            type: 'scatter',
            mode: 'lines',
            name: vehicleName,
            line: {
              color: this.getVehicleColor(index),
              width: 8
            }
          };
        });

        const combinedPlotData = [...fleetData, ...averageLines];
        this.updateChart(combinedPlotData);
        this.dataLoading = false;
      },
      error: (error) => {
        console.error('Error fetching Current Vehicle Info data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    });
  }

  getVehicleColor(index: number): string {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    return colors[index % 3];
  }

  plotCurrentSohcChart(datapoints: any): any[] {
    const transformXAxisPipe = new TransformXAxisPipe();
    const data = Object.keys(datapoints).flatMap((fleetId: string) => {
      const plotData = datapoints[fleetId];
      const sohc = plotData.map((dataObject: any) => dataObject.binSoh);
      const percentAmount = plotData.map((dataObject: any) => dataObject.percVeh);
      this.chartRange = transformXAxisPipe.transform(sohc, percentAmount, 3,100);
      return {
        x: sohc,
        y: percentAmount,
        type: 'bar',
        name: `Fleet`,
        marker: {
          color: '#8A9097'
        }
      };
    });
    return data;
  }

  updateChart(data: any[]): void {
    const layout = {
      title: 'Current State of Health',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'State of Health [%]',
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Fleet Vehicles [%]'
      },
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('current-sohc-chart', data, layout);
  }
}
